import React, { useState } from 'react'
import { X, Search } from 'lucide-react'

interface CreateUnitModalProps {
  unitType: 'course' | 'group' | 'class'
  onClose: () => void
  onSave: (data: any) => void
  subjects?: any[]
  curricula?: any[]
  jahrgang?: any[]
}

export default function CreateUnitModal({ 
  unitType, 
  onClose, 
  onSave, 
  subjects = [], 
  curricula = [], 
  jahrgang = [] 
}: CreateUnitModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    beschreibung: '',
    // Kurs-spezifisch
    fach: [] as string[],
    jahrgang: '',
    // Klassen-spezifisch
    class_name: '',
    grade_level: '',
    department: '',
    room: '',
    class_teacher_id: null as number | null,
    // Gruppen-spezifisch
    gruppen_typ: 'arbeitsgruppe',
    // Gemeinsam
    farbe: unitType === 'course' ? '#3B82F6' : unitType === 'group' ? '#10B981' : '#8B5CF6',
    icon: unitType === 'course' ? 'book' : unitType === 'group' ? 'users' : 'graduation-cap',
    max_teilnehmer: unitType === 'course' ? 30 : unitType === 'group' ? 10 : 30,
    curricula: [] as number[]
  })

  // Modal states (für Kurse)
  const [showSubjectModal, setShowSubjectModal] = useState(false)
  const [showCurriculumModal, setShowCurriculumModal] = useState(false)
  const [subjectSearchTerm, setSubjectSearchTerm] = useState('')
  const [curriculumSearchTerm, setCurriculumSearchTerm] = useState('')
  const [selectedSubjects, setSelectedSubjects] = useState<string[]>(formData.fach)
  const [selectedCurricula, setSelectedCurricula] = useState<number[]>(formData.curricula)

  // Filter functions
  const filteredSubjects = subjects.filter((subject: any) => {
    if (!subject || !subject.name) return false
    const search = subjectSearchTerm.toLowerCase()
    return subject.name.toLowerCase().includes(search)
  })

  const filteredCurricula = curricula.filter((curriculum: any) => {
    if (!curriculum || !curriculum.name) return false
    const search = curriculumSearchTerm.toLowerCase()
    return (
      curriculum.name.toLowerCase().includes(search) ||
      (curriculum.subject && curriculum.subject.toLowerCase().includes(search)) ||
      (curriculum.exam_type && curriculum.exam_type.toLowerCase().includes(search))
    )
  })

  // Subject selection functions
  const toggleSubject = (subjectName: string) => {
    const newSubjects = selectedSubjects.includes(subjectName)
      ? selectedSubjects.filter(name => name !== subjectName)
      : [...selectedSubjects, subjectName]
    setSelectedSubjects(newSubjects)
    setFormData({ ...formData, fach: newSubjects })
  }

  const handleSelectAllSubjects = () => {
    if (selectedSubjects.length === filteredSubjects.length) {
      setSelectedSubjects([])
      setFormData({ ...formData, fach: [] })
    } else {
      const allSubjectNames = filteredSubjects.map((s: any) => s.name)
      setSelectedSubjects(allSubjectNames)
      setFormData({ ...formData, fach: allSubjectNames })
    }
  }

  const handleSubjectSelection = () => {
    setFormData({ ...formData, fach: selectedSubjects })
    setShowSubjectModal(false)
  }

  // Curriculum selection functions
  const toggleCurriculum = (curriculumId: number) => {
    const newCurricula = selectedCurricula.includes(curriculumId)
      ? selectedCurricula.filter(id => id !== curriculumId)
      : [...selectedCurricula, curriculumId]
    setSelectedCurricula(newCurricula)
    setFormData({ ...formData, curricula: newCurricula })
  }

  const handleSelectAllCurricula = () => {
    if (selectedCurricula.length === filteredCurricula.length) {
      setSelectedCurricula([])
      setFormData({ ...formData, curricula: [] })
    } else {
      const allCurriculumIds = filteredCurricula.map((c: any) => c.id)
      setSelectedCurricula(allCurriculumIds)
      setFormData({ ...formData, curricula: allCurriculumIds })
    }
  }

  const handleCurriculumSelection = () => {
    setFormData({ ...formData, curricula: selectedCurricula })
    setShowCurriculumModal(false)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    // Bereite Daten für API vor
    let submitData: any = {
      name: unitType === 'class' ? formData.class_name : formData.name,
      beschreibung: formData.beschreibung,
      farbe: formData.farbe,
      icon: formData.icon,
      max_teilnehmer: formData.max_teilnehmer
    }

    if (unitType === 'course') {
      submitData.fach = formData.fach.join(',')
      submitData.jahrgang = formData.jahrgang
    } else if (unitType === 'class') {
      submitData.class_name = formData.class_name
      submitData.grade_level = formData.grade_level
      submitData.department = formData.department
      submitData.room = formData.room
      submitData.class_teacher_id = formData.class_teacher_id
    } else if (unitType === 'group') {
      submitData.gruppen_typ = formData.gruppen_typ
    }

    onSave(submitData)
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name/Class Name */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          {unitType === 'class' ? 'Klassenname' : 'Name'} *
        </label>
        <input
          type="text"
          required
          value={unitType === 'class' ? formData.class_name : formData.name}
          onChange={(e) => setFormData({ 
            ...formData, 
            [unitType === 'class' ? 'class_name' : 'name']: e.target.value 
          })}
          className="input-field"
        />
      </div>

      {/* Beschreibung */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Beschreibung
        </label>
        <textarea
          value={formData.beschreibung}
          onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
          rows={3}
          className="input-field"
        />
      </div>

      {/* Kurs-spezifische Felder */}
      {unitType === 'course' && (
        <>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Fächer
            </label>
            <button
              type="button"
              onClick={() => setShowSubjectModal(true)}
              className="input-field text-left hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {formData.fach.length > 0 
                ? `${formData.fach.length} Fächer ausgewählt`
                : 'Fächer auswählen...'
              }
            </button>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Jahrgang
            </label>
            <select
              value={formData.jahrgang}
              onChange={(e) => setFormData({ ...formData, jahrgang: e.target.value })}
              className="input-field"
            >
              <option value="">Kein Jahrgang</option>
              {jahrgang.map((jg: any) => (
                <option key={jg.id} value={jg.jahrgang}>
                  {jg.jahrgang}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      {/* Klassen-spezifische Felder */}
      {unitType === 'class' && (
        <>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Stufe
            </label>
            <input
              type="text"
              value={formData.grade_level}
              onChange={(e) => setFormData({ ...formData, grade_level: e.target.value })}
              placeholder="z.B. 5, 6, 7..."
              className="input-field"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Abteilung
            </label>
            <input
              type="text"
              value={formData.department}
              onChange={(e) => setFormData({ ...formData, department: e.target.value })}
              placeholder="z.B. MINT, Sprachen..."
              className="input-field"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Raum
            </label>
            <input
              type="text"
              value={formData.room}
              onChange={(e) => setFormData({ ...formData, room: e.target.value })}
              placeholder="z.B. A101"
              className="input-field"
            />
          </div>
        </>
      )}

      {/* Gruppen-spezifische Felder */}
      {unitType === 'group' && (
        <div>
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Gruppentyp
          </label>
          <select
            value={formData.gruppen_typ}
            onChange={(e) => setFormData({ ...formData, gruppen_typ: e.target.value })}
            className="input-field"
          >
            <option value="arbeitsgruppe">Arbeitsgruppe</option>
            <option value="projektgruppe">Projektgruppe</option>
            <option value="lernpartner">Lernpartner</option>
            <option value="sitzgruppe">Sitzgruppe</option>
            <option value="sonstige">Sonstige</option>
          </select>
        </div>
      )}

      {/* Gemeinsame Felder */}
      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Farbe
        </label>
        <input
          type="color"
          value={formData.farbe}
          onChange={(e) => setFormData({ ...formData, farbe: e.target.value })}
          className="input-field h-10"
        />
      </div>

      <div>
        <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Maximale Teilnehmer
        </label>
        <input
          type="number"
          min="1"
          value={formData.max_teilnehmer}
          onChange={(e) => setFormData({ ...formData, max_teilnehmer: parseInt(e.target.value) || 0 })}
          className="input-field"
        />
      </div>

      {/* Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        <button
          type="button"
          onClick={onClose}
          className="btn btn-secondary btn-sm"
        >
          Abbrechen
        </button>
        <button
          type="submit"
          className="btn btn-primary btn-sm"
        >
          Erstellen
        </button>
      </div>

      {/* Subject Selection Modal (für Kurse) */}
      {showSubjectModal && unitType === 'course' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Fächer auswählen</h3>
              <button
                type="button"
                onClick={() => setShowSubjectModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={subjectSearchTerm}
                  onChange={(e) => setSubjectSearchTerm(e.target.value)}
                  placeholder="Fächer suchen..."
                  className="input-field pl-10"
                />
              </div>
            </div>

            <div className="mb-4">
              <button
                type="button"
                onClick={handleSelectAllSubjects}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {selectedSubjects.length === filteredSubjects.length ? 'Alle abwählen' : 'Alle auswählen'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto mb-4">
              <div className="space-y-2">
                {filteredSubjects.map((subject: any) => (
                  <label
                    key={subject.id}
                    className="flex cursor-pointer items-center rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={selectedSubjects.includes(subject.name)}
                      onChange={() => toggleSubject(subject.name)}
                      className="mr-3"
                    />
                    <span>{subject.name}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowSubjectModal(false)}
                className="btn btn-secondary btn-sm"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleSubjectSelection}
                className="btn btn-primary btn-sm"
              >
                Auswählen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Curriculum Selection Modal (für Kurse) */}
      {showCurriculumModal && unitType === 'course' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 dark:bg-black/70">
          <div className="card flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-50">Curricula auswählen</h3>
              <button
                type="button"
                onClick={() => setShowCurriculumModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={curriculumSearchTerm}
                  onChange={(e) => setCurriculumSearchTerm(e.target.value)}
                  placeholder="Curricula suchen..."
                  className="input-field pl-10"
                />
              </div>
            </div>

            <div className="mb-4">
              <button
                type="button"
                onClick={handleSelectAllCurricula}
                className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
              >
                {selectedCurricula.length === filteredCurricula.length ? 'Alle abwählen' : 'Alle auswählen'}
              </button>
            </div>

            <div className="flex-1 overflow-y-auto mb-4">
              <div className="space-y-2">
                {filteredCurricula.map((curriculum: any) => (
                  <label
                    key={curriculum.id}
                    className="flex cursor-pointer items-center rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
                  >
                    <input
                      type="checkbox"
                      checked={selectedCurricula.includes(curriculum.id)}
                      onChange={() => toggleCurriculum(curriculum.id)}
                      className="mr-3"
                    />
                    <div>
                      <span className="font-medium">{curriculum.name}</span>
                      {curriculum.subject && (
                        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">({curriculum.subject})</span>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowCurriculumModal(false)}
                className="btn btn-secondary btn-sm"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleCurriculumSelection}
                className="btn btn-primary btn-sm"
              >
                Auswählen
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}





